/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Modal, BaseModalProps } from '@blend/shared-react';
import { colors, mixins } from '@blend/consumer-components';
import { css, StyleSheet } from 'aphrodite';
import { getFormValues, InjectedFormProps, reduxForm, SubmissionError } from 'redux-form';

import { connect } from 'react-redux';
import { TaskForm } from '../../form/TaskForm';
import { InputConfig } from '../../form/TaskInputs/types';

import { notaryAPI } from '../../../api/notary';
import { State } from '../../../redux';
import { TaskInputs } from '../../../components/form/TaskInputs/TaskInputs';
import { FeedbackIssueType } from '../../../shared/constants/room';
import { getFilteredInputs } from '../../../utils/formUtils';
import { Wrapper, Error } from '@components/form/Inputs/InputError';
import { DefaultSubmitInput } from '@components/form/DefaultSubmitInput/DefaultSubmitInput';

const FORM_NAME = 'notary_feedback_modal';

const overlayStyleConfig = {
  backgroundColor: colors.catalyst,
};

const styles = StyleSheet.create({
  contentWrapper: {
    ...mixins.margin(40, 75, 40, 75),
  },
  headerText: {
    fontSize: 24,
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 40,
  },
  helperText: {
    fontSize: 16,
    lineHeight: '22px',
    marginBottom: 24,
  },
  buttonWrapper: {
    marginTop: 30,
  },
  submitDescription: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    color: colors.catalystLight80,
    ...mixins.margin(30, 0, 0, 0),
  },
  feedbackWrapper: {
    ...mixins.margin(16, 0, 0, 0),
  },
});

export namespace FeedbackModal {
  export interface OwnProps extends Omit<BaseModalProps, 'handleClose'> {
    callId?: string;
    satisfactionScore: number;
    onSubmitSuccess: () => void;
    isSubmitting: boolean;
    setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  }
  export interface StateProps {
    formValues: any;
  }

  export type Props = InjectedFormProps<{}, StateProps & OwnProps> & OwnProps & StateProps;
}

const inputConfigs: InputConfig[] = [
  {
    id: 'issuesEncountered',
    type: 'multi_checkbox',
    choices: [
      { text: 'Identity verification process', value: FeedbackIssueType.ID_VERIFICATION },
      { text: 'Document signing process', value: FeedbackIssueType.DOCUMENT_SIGNING },
      { text: 'Video and audio quality', value: FeedbackIssueType.VIDEO_AUDIO_QUALITY },
    ],
    required: false,
    display: 'issuesEncountered',
    text: 'Issues Encountered',
    label: 'Did you have issues with any of the following during your closing session?',
  },
  { label: 'Do you have any additional feedback for us?', id: 'additionalFeedback', type: 'text', required: false },
];

const _FeedbackModal: React.FC<FeedbackModal.Props> = ({ handleSubmit, error, formValues, ...props }) => {
  const { callId, satisfactionScore, onSubmitSuccess, isSubmitting, setIsSubmitting, ...otherProps } = props;
  const [inputs, setInputs] = useState<InputConfig[]>([]);

  const formTemplate = (
    <>
      <TaskInputs inputs={inputs} data-it="feedbackInputs" />
      <div className={css(styles.buttonWrapper)}>
        <DefaultSubmitInput disabled={isSubmitting}>Submit</DefaultSubmitInput>
      </div>
    </>
  );

  useEffect(() => {
    const filteredInputs = getFilteredInputs(inputConfigs, formValues);
    setInputs(filteredInputs);
  }, [formValues]);

  return (
    <Modal overlayStyleConfig={overlayStyleConfig} data-it="FeedbackModal" handleClose={() => {}} {...otherProps}>
      <div className={css(styles.contentWrapper)}>
        <div className={css(styles.headerText)}>We would appreciate your feedback</div>
        <TaskForm onSubmit={handleSubmit} formTemplate={formTemplate} />
        {error && (
          <Wrapper id={'general-error'}>
            <Error error={error} />
          </Wrapper>
        )}
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: State) => {
  return {
    formValues: getFormValues(FORM_NAME)(state),
  };
};

export const FeedbackModal = connect(mapStateToProps)(
  reduxForm<{}, FeedbackModal.OwnProps & FeedbackModal.StateProps>({
    form: FORM_NAME,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    onSubmit: async (
      formValues: any,
      _dispatch: any,
      {
        callId,
        satisfactionScore,
        onSubmitSuccess,
        setIsSubmitting,
      }: {
        callId?: string;
        satisfactionScore: number;
        onSubmitSuccess: () => void;
        setIsSubmitting: Dispatch<SetStateAction<boolean>>;
      },
    ) => {
      setIsSubmitting(true);
      const generalErrorMessage = `
      An error occurred during submission. Please refresh the page and try
      again, or contact us if you still have trouble.
    `;
      const { issuesEncountered, additionalFeedback } = formValues;

      try {
        const requestbody = { satisfactionScore, issues: issuesEncountered, userFeedback: additionalFeedback };
        await notaryAPI.calls.createNotaryFeedback(callId!, requestbody);
        onSubmitSuccess();
      } catch (e) {
        setIsSubmitting(false);
        throw new SubmissionError({ _error: generalErrorMessage });
      }
    },
  })(_FeedbackModal),
);
